import axios from "axios";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@/lib/safeStorage";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://radar-api.aquivolabs.com";

const defaultHeaders = {
  "Content-Type": "application/json",
  "X-API-Key": process.env.REACT_APP_RADAR_API_KEY || "dev_key_placeholder",
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { ...defaultHeaders },
});

/** No session cookies / no JWT — for public marketing tools (e.g. Meta Ads Library search). */
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: false,
  headers: { ...defaultHeaders },
});

/**
 * Separate client for refresh only — must NOT go through the 401 response interceptor
 * or a failed refresh re-enters the interceptor and causes a request storm.
 */
const apiForRefresh = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { ...defaultHeaders },
});

let refreshInFlight = null;

async function getNewAccessTokenViaRefresh() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const res = await apiForRefresh.post("/api/web/auth/refresh", {});
      return res.data?.accessToken ?? null;
    } catch (firstError) {
      const legacyRefresh = safeGetItem("refresh_token");
      if (!legacyRefresh) {
        throw firstError;
      }
      const res = await apiForRefresh.post("/api/web/auth/refresh", {
        refreshToken: legacyRefresh,
      });
      return res.data?.accessToken ?? null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// JWT token interceptor for automatic authorization
api.interceptors.request.use((config) => {
  const token = safeGetItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry) {
      const reqUrl = `${original.baseURL || ""}${original.url || ""}`;
      if (/\/api\/web\/auth\/(login|register|refresh)/.test(reqUrl)) {
        return Promise.reject(error);
      }

      const hasAccess =
        typeof window !== "undefined" && !!safeGetItem("access_token");
      const hasLegacyRefresh =
        typeof window !== "undefined" && !!safeGetItem("refresh_token");

      // Logged-out /me → 401 is normal; do not call refresh (avoids 400/429 storms).
      if (!hasAccess && !hasLegacyRefresh) {
        return Promise.reject(error);
      }

      original._retry = true;

      try {
        const accessToken = await getNewAccessTokenViaRefresh();
        if (accessToken) {
          safeSetItem("access_token", accessToken);
          original.headers.Authorization = `Bearer ${accessToken}`;
          return api(original);
        }
      } catch (refreshError) {
        safeRemoveItem("access_token");
        safeRemoveItem("refresh_token");
        const path = window.location.pathname.replace(/\/$/, "") || "/";
        const onPublicAuth =
          path === "/" ||
          path === "/login" ||
          path === "/register" ||
          path === "/reset-password";
        if (!onPublicAuth) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Authentication Token Management
 */

export function setTokens(accessToken, refreshToken) {
  safeSetItem('access_token', accessToken);
  safeSetItem('refresh_token', refreshToken);
}

export function clearTokens() {
  safeRemoveItem('access_token');
  safeRemoveItem('refresh_token');
}

export function getTokens() {
  return {
    accessToken: safeGetItem('access_token'),
    refreshToken: safeGetItem('refresh_token'),
  };
}

export function isAuthenticated() {
  const { accessToken } = getTokens();
  return !!accessToken;
}

/**
 * Authentication API Functions
 */

export async function login({ email, password }) {
  const response = await api.post("/api/web/auth/login", { email, password });
  const { accessToken } = response.data;

  // Remix sends refresh token only as HttpOnly Set-Cookie (not in JSON).
  if (accessToken) {
    safeSetItem("access_token", accessToken);
  }

  return response.data;
}

export async function signup({ email, password, firstName, lastName, name }) {
  const trimmed = (name || "").trim();
  const parts = trimmed ? trimmed.split(/\s+/) : [];
  const fn = firstName ?? (parts[0] || undefined);
  const ln = lastName ?? (parts.length > 1 ? parts.slice(1).join(" ") : undefined);
  const response = await api.post("/api/web/auth/register", {
    email,
    password,
    firstName: fn,
    lastName: ln,
  });
  const { accessToken } = response.data;

  if (accessToken) {
    safeSetItem("access_token", accessToken);
  }

  return response.data;
}

export async function logout() {
  const response = await api.post("/api/web/auth/logout");
  clearTokens();
  return response.data;
}

export async function refreshTokenFunction(refreshToken) {
  const response = await api.post("/api/web/auth/refresh", { refreshToken });
  return response.data;
}

export async function getCurrentUser() {
  const response = await api.get("/api/web/me");
  return response.data;
}

/**
 * Competitor API Functions
 */

export async function listCompetitors(shop = null) {
  const params = shop ? { shop } : {};
  const response = await api.get("/api/competitors", { params });
  return response.data;
}

export async function searchCompetitors(brandName, options = {}) {
  const response = await api.post("/api/competitors/search", { brandName, options });
  return response.data;
}

export async function createCompetitor(payload, shop = null) {
  const params = shop ? { shop } : {};
  const response = await api.post("/api/competitors", payload, { params });
  return response.data.data;
}

export async function getCompetitorDetails(competitorId, shop = null) {
  const params = shop ? { shop } : {};
  const response = await api.get(`/api/competitors/${competitorId}`, { params });
  return response.data;
}

export async function getCompetitorAds(competitorId, { status = "ACTIVE", limit = 25 } = {}) {
  const params = { ad_active_status: status, limit };
  const response = await api.get(`/api/competitors/${competitorId}/ads`, { params });
  return response.data;
}

export async function resyncCompetitorAds(competitorId) {
  const response = await api.post(`/api/competitors/${competitorId}/resync`);
  return response.data;
}

export async function updateCompetitorStatus(competitorId, status) {
  const response = await api.put(`/api/competitors/${competitorId}`, { status });
  return response.data;
}

export async function getDetectedCompetitors(shop = null) {
  const params = shop ? { shop } : {};
  const response = await api.get("/api/competitors", { params });
  return response.data;
}

export async function triggerCompetitorDetection(shop = null) {
  const params = shop ? { shop } : {};
  const response = await api.post("/api/competitors/detect", {}, { params });
  return response.data;
}

export async function searchNewCompetitors(searchTerm, category = null) {
  const response = await api.post("/api/competitors/search", {
    search_term: searchTerm,
    category
  });
  return response.data;
}

/**
 * Analysis & Intelligence API Functions
 */

export async function listProducts(params = {}) {
  const response = await api.get("/api/analysis/categories", { params });
  return response.data;
}

export async function getProductCompetitors(productCategory, keywords, competitors = []) {
  const response = await api.post("/api/products/competitors", {
    productCategory,
    keywords,
    competitors,
  });
  return response.data;
}

export async function predictPerformance(adCreative, productCategory, targetAudience) {
  const response = await api.post("/api/predict/performance", {
    adCreative,
    productCategory,
    targetAudience,
  });
  return response.data;
}

export async function calculateRevenue(competitorData, storeMetrics) {
  const response = await api.post("/api/revenue/calculate", {
    competitorData,
    storeMetrics,
  });
  return response.data;
}

export async function searchMetaAds({
  searchTerms,
  countryCode,
  status = "ACTIVE",
  limit = 20,
  /** false = return Meta’s raw list (no keyword match on page/copy we show). */
  strictKeyword = true,
}) {
  const params = {
    search_terms: searchTerms,
    country: countryCode.toUpperCase(),
    ad_active_status: status,
    limit,
    strict_keyword: strictKeyword ? "1" : "0",
  };
  const response = await publicApi.get("/api/meta/ads", { params });
  return response.data;
}

export async function getKeywordStats(query) {
  const response = await api.get("/keywords", { params: { q: query } });
  return response.data;
}

export async function analyzeCreative(payload) {
  const response = await api.post("/ai/creative-analysis", payload);
  return response.data;
}

export async function generateScripts(payload) {
  const response = await api.post("/ai/script-generation", payload);
  return response.data;
}

export async function searchTikTokAds({ query, country }) {
  const response = await api.get("/tiktok/ads", {
    params: { query, country },
  });
  return response.data;
}

export async function searchGoogleAds({ query, country }) {
  const response = await api.get("/google/ads", {
    params: { query, country },
  });
  return response.data;
}

/**
 * Shopify Integration API Functions
 */

export async function connectShopifyStore(shopDomain) {
  const response = await api.post("/api/shopify/connect", {
    shop_domain: shopDomain
  });
  return response.data;
}

export async function getShopifyConnectionStatus() {
  const response = await api.get("/api/shopify/status");
  return response.data;
}

/**
 * Redeem a Shopify connect ID (generated by the Shopify app) to link
 * the current Shopify shop to the logged-in Radar user.
 */
export async function redeemShopifyConnectId(connectId) {
  const response = await api.post("/api/shopify-connect/redeem", { connectId });
  return response.data;
}

export async function disconnectShopifyStore(shopDomain) {
  const response = await api.delete("/api/shopify/disconnect", {
    data: { shop_domain: shopDomain }
  });
  return response.data;
}

export async function triggerShopifySync(syncType = 'manual') {
  const response = await api.post("/api/shopify/sync", {
    sync_type: syncType
  });
  return response.data;
}

export async function getShopifyLogs(limit = 20, offset = 0) {
  const response = await api.get("/api/shopify/logs", {
    params: { limit, offset }
  });
  return response.data;
}

/**
 * Alerts & Signals API Functions
 */

export async function getAlerts(limit = 50, shop = null) {
  const params = { limit };
  if (shop) params.shop = shop;
  const response = await api.get("/api/alerts", { params });
  return response.data;
}

export async function getScalingSignals() {
  const response = await api.get("/signals");
  return response.data;
}

export async function getDashboardSummary() {
  const response = await api.get("/dashboard/summary");
  return response.data;
}

export async function getBestSellers(shop = null, limit = 5) {
  const params = { limit };
  if (shop) params.shop = shop;
  const response = await api.get("/api/analysis/best-sellers", { params });
  return response.data;
}

export async function getMarketBenchmark(adUrl, productId = null) {
  const params = { ad_url: adUrl };
  if (productId) params.product_id = productId;
  const response = await api.get("/api/competitors/benchmark", { params });
  return response.data;
}

/**
 * Settings API Functions
 */

export async function getAlertSettings() {
  const response = await api.get("/settings/alerts");
  return response.data;
}

export async function updateAlertSettings(payload) {
  const response = await api.put("/settings/alerts", payload);
  return response.data;
}

export async function getAccountUsage() {
  const response = await api.get("/account/usage");
  return response.data;
}

/**
 * Waitlist API Functions
 */

export async function submitWaitlistForm({ email, variant, selectedUseCase }) {
  const response = await publicApi.post("/api/web/waitlist", {
    email,
    variant,
    selectedUseCase,
    metadata: {
      userAgent: navigator?.userAgent,
      referrer: document?.referrer,
      timestamp: new Date().toISOString(),
    }
  });
  return response.data;
}
